import {
  CalendarCheck,
  ChartBar,
  ChartLine,
  Flag,
  HandHeart,
  Lightning,
  MapPin,
  UsersThree,
} from '@phosphor-icons/react';
import { useState } from 'react';
import { admin } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { useAsync } from '../../hooks/useAsync';
import type { LigneDeMembre, Part, ServeurDuTableau } from '../../firebase/tableau';
import { t } from '../../i18n';

/**
 * Le tableau de bord de celui qui tient l'application.
 *
 * Il repond a quatre questions et s'arrete la : qui vient encore, sur
 * quelle version, qu'est-ce qui se prepare, et qu'est-ce qui attend une
 * decision. Rien du contenu de personne — ni message, ni post, ni avec
 * qui l'on parle : savoir que la maison tourne ne demande pas d'ouvrir
 * les chambres.
 *
 * Le calcul se fait au serveur : lire la fiche de tout le monde n'est pas
 * un droit qu'on donne a un telephone, meme au sien.
 */

/** « il y a 12 min », « il y a 5 h », « il y a 3 j ». */
function depuis(quand: number): string {
  if (!quand) return t.profil.tableau.jamais;
  const minutes = Math.round((Date.now() - quand) / 60000);
  if (minutes < 60) return t.profil.tableau.depuis.minutes(Math.max(minutes, 1));
  const heures = Math.round(minutes / 60);
  if (heures < 48) return t.profil.tableau.depuis.heures(heures);
  return t.profil.tableau.depuis.jours(Math.round(heures / 24));
}

function ligneDeMembre(membre: LigneDeMembre) {
  return (
    <li key={membre.pseudo} className="tb-ligne">
      <span className="tb-ligne__nom">{membre.pseudo}</span>
      <span className="tb-ligne__detail">
        {membre.spot ?? t.profil.tableau.sansServeur}
        {membre.coquille ? t.profil.tableau.build(String(membre.coquille)) : ''}
      </span>
      <span className="tb-ligne__quand">{depuis(membre.vuA)}</span>
    </li>
  );
}

/**
 * Les inscriptions des huit dernieres semaines, en barres.
 *
 * Une seule serie, donc ni legende ni chiffre sur chaque barre : la
 * semaine choisie dit le sien au-dessus, et toucher une autre barre la
 * choisit. La plus recente l'est d'abord — c'est elle qu'on vient voir.
 */
function Inscriptions({ semaines }: { semaines: number[] }) {
  const derniere = semaines.length - 1;
  const [choisie, setChoisie] = useState(derniere);
  const plusHaute = Math.max(1, ...semaines);
  const quand = (i: number) =>
    i === derniere ? t.profil.tableau.cetteSemaine : t.profil.tableau.ilYaSemaines(derniere - i);

  return (
    <div className="tb-graphe">
      <p className="tb-graphe__valeur">
        <strong>{t.profil.tableau.nInscriptions(semaines[choisie] ?? 0)}</strong> · {quand(choisie)}
      </p>
      <div className="tb-graphe__barres">
        {semaines.map((n, i) => (
          <button
            key={i}
            type="button"
            className={`tb-graphe__barre${i === choisie ? ' tb-graphe__barre--choisie' : ''}`}
            aria-label={`${quand(i)} : ${t.profil.tableau.nInscriptions(n)}`}
            aria-pressed={i === choisie}
            onClick={() => setChoisie(i)}
          >
            <span style={{ height: `${(n / plusHaute) * 100}%` }} />
          </button>
        ))}
      </div>
      <div className="tb-graphe__axe">
        <span>{quand(0)}</span>
        <span>{quand(derniere)}</span>
      </div>
    </div>
  );
}

/** Une part en pourcentage, et le compte qui la fonde. */
function Fidelite({ part, quoi }: { part: Part; quoi: string }) {
  return (
    <div className="tb-chiffre">
      <span className="tb-chiffre__valeur">
        {part.sur > 0 ? `${Math.round((part.n / part.sur) * 100)} %` : '–'}
      </span>
      <span className="tb-chiffre__quoi">{quoi}</span>
      <span className="tb-chiffre__base">
        {part.sur > 0 ? t.profil.tableau.surN(part.n, part.sur) : t.profil.tableau.pasAssez}
      </span>
    </div>
  );
}

function ligneDeServeur(serveur: ServeurDuTableau) {
  return (
    <li key={serveur.nom} className="tb-ligne tb-ligne--serveur">
      <span className="tb-ligne__nom">{serveur.nom}</span>
      <span className="tb-ligne__detail">
        {t.profil.tableau.serveurLigne(serveur.membres, serveur.actifs7j)}
      </span>
      <span className="tb-ligne__detail">
        {t.profil.tableau.serveurDetail(
          serveur.posts7j,
          serveur.tenues30j,
          serveur.presences30j,
          serveur.aVenir,
        )}
      </span>
    </li>
  );
}

export function TableauScreen() {
  const { data, loading, error, reload } = useAsync(() => admin.tableauDeBord(), [], 'tableau');

  return (
    <SubScreen title={t.profil.tableau.titre}>
      {loading && !data && <ScreenLoader />}
      {error && !data && <ScreenError message={error} onRetry={reload} />}

      {data && (
        <>
          {/* Quatre nombres, la ou l'on regarde en premier. */}
          <div className="tb-chiffres">
            <div className="tb-chiffre">
              <span className="tb-chiffre__valeur">{data.membres.actifs24h}</span>
              <span className="tb-chiffre__quoi">{t.profil.tableau.actifsAujourdhui}</span>
            </div>
            <div className="tb-chiffre">
              <span className="tb-chiffre__valeur">{data.membres.actifs7j}</span>
              <span className="tb-chiffre__quoi">{t.profil.tableau.semaine}</span>
            </div>
            <div className="tb-chiffre">
              <span className="tb-chiffre__valeur">{data.membres.total}</span>
              <span className="tb-chiffre__quoi">{t.profil.tableau.comptes}</span>
            </div>
            <div className="tb-chiffre">
              <span className="tb-chiffre__valeur">{data.membres.nouveaux7j}</span>
              <span className="tb-chiffre__quoi">{t.profil.tableau.nouveaux}</span>
            </div>
          </div>

          {data.signalements.ouverts > 0 && (
            <p className="tb-alerte">
              <Flag size={16} weight="fill" />
              {data.signalements.ouverts === 1
                ? t.profil.tableau.unSignalement
                : t.profil.tableau.signalements(data.signalements.ouverts)}
            </p>
          )}

          {/*
            Ce qui s'est passe, et non qui est passe : la vie du mois, la
            courbe des arrivees, ce qui en reste, et ou cela se joue.
          */}
          <h2 className="tb-titre">
            <CalendarCheck size={18} weight="bold" />
            {t.profil.tableau.mois}
          </h2>
          <div className="tb-chiffres tb-chiffres--trois">
            <div className="tb-chiffre">
              <span className="tb-chiffre__valeur">{data.mois.posts}</span>
              <span className="tb-chiffre__quoi">{t.profil.tableau.posts}</span>
            </div>
            <div className="tb-chiffre">
              <span className="tb-chiffre__valeur">{data.mois.tenues}</span>
              <span className="tb-chiffre__quoi">{t.profil.tableau.tenues}</span>
            </div>
            <div className="tb-chiffre">
              <span className="tb-chiffre__valeur">{data.mois.presences}</span>
              <span className="tb-chiffre__quoi">{t.profil.tableau.presences}</span>
            </div>
          </div>

          <h2 className="tb-titre">
            <ChartBar size={18} weight="bold" />
            {t.profil.tableau.inscriptions}
          </h2>
          <Inscriptions semaines={data.inscriptions} />

          <h2 className="tb-titre">
            <HandHeart size={18} weight="bold" />
            {t.profil.tableau.fidelite}
          </h2>
          <div className="tb-chiffres">
            <Fidelite part={data.retention.revenus} quoi={t.profil.tableau.revenus} />
            <Fidelite part={data.retention.encoreActifs} quoi={t.profil.tableau.encoreActifs} />
          </div>

          <h2 className="tb-titre">
            <MapPin size={18} weight="bold" />
            {t.profil.tableau.parServeur}
          </h2>
          <ul className="tb-liste">{data.parServeur.map(ligneDeServeur)}</ul>

          <h2 className="tb-titre">
            <UsersThree size={18} weight="bold" />
            {t.profil.tableau.connexions}
          </h2>
          <ul className="tb-liste">{data.membres.derniers.map(ligneDeMembre)}</ul>

          <h2 className="tb-titre">
            <ChartLine size={18} weight="bold" />
            {t.profil.tableau.versions}
          </h2>
          <ul className="tb-liste">
            {data.versions.map((version) => (
              <li key={version.nom} className="tb-ligne">
                <span className="tb-ligne__nom">{version.nom}</span>
                <span className="tb-ligne__quand">
                  {version.combien === 1 ? t.profil.tableau.unCompte : t.profil.tableau.nComptes(version.combien)}
                </span>
              </li>
            ))}
          </ul>

          <h2 className="tb-titre">
            <Lightning size={18} weight="bold" />
            {t.profil.tableau.sorties}
            <span className="tb-titre__compte">{data.sorties.aVenir}</span>
          </h2>
          {data.sorties.prochaines.length === 0 ? (
            <p className="tb-vide">{t.profil.tableau.rien}</p>
          ) : (
            <ul className="tb-liste">
              {data.sorties.prochaines.map((sortie) => (
                <li key={`${sortie.date}${sortie.heure}${sortie.titre}`} className="tb-ligne">
                  <span className="tb-ligne__nom">
                    {sortie.titre}
                    {sortie.spontane && <em className="tb-spontane">{t.profil.tableau.spontanee}</em>}
                  </span>
                  <span className="tb-ligne__detail">
                    {sortie.date.split('-').reverse().slice(0, 2).join('/')} · {sortie.heure} ·{' '}
                    {sortie.spot}
                  </span>
                  <span className="tb-ligne__quand">
                    {sortie.inscrits}/{sortie.places}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </SubScreen>
  );
}
